import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { BottomSheet } from "../components/BottomSheet.jsx";
import { Button } from "../components/Button.jsx";
import { getSecuritySettings, verifyDeletePassword } from "../../features/settings/services/settingsService.js";

const DangerConfirmContext = createContext(null);

/**
 * Confirmation for destructive actions (deletes, voiding/resetting payments).
 * - Delete protection OFF: the familiar browser confirm box.
 * - Delete protection ON: a sheet that requires the business's delete password.
 */
export function DangerConfirmProvider({ children }) {
  const [isProtected, setIsProtected] = useState(false);
  const [request, setRequest] = useState(null); // { title, message, confirmLabel, resolve }
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const inputRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const settings = await getSecuritySettings();
      setIsProtected(settings.deleteProtection);
      return settings.deleteProtection;
    } catch (err) {
      console.warn("Could not load security settings:", err.message);
      return isProtected;
    }
  }, [isProtected]);

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    if (request) setTimeout(() => inputRef.current?.focus(), 50);
  }, [request]);

  const confirmDanger = useCallback(async ({ title = "Are you sure?", message = "", confirmLabel = "Delete" } = {}) => {
    // Always use the latest setting (another user may have turned it on/off)
    const protectedNow = await refresh();
    if (!protectedNow) return window.confirm(message || title);
    return new Promise((resolve) => {
      setPassword("");
      setError("");
      setRequest({ title, message, confirmLabel, resolve });
    });
  }, [refresh]);

  const close = (result) => {
    request?.resolve(result);
    setRequest(null);
    setPassword("");
    setError("");
  };

  const submit = async () => {
    if (!password) {
      setError("Enter the delete password.");
      return;
    }
    setChecking(true);
    try {
      if (await verifyDeletePassword(password)) close(true);
      else {
        setError("Wrong password. Nothing was deleted.");
        setPassword("");
        inputRef.current?.focus();
      }
    } catch (err) {
      setError(err.message || "Could not check the password.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <DangerConfirmContext.Provider value={{ confirmDanger, isProtected, verify: verifyDeletePassword, refresh }}>
      {children}
      <BottomSheet isOpen={!!request} onClose={() => close(false)} title={`🗑️ ${request?.title || ""}`}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}
        >
          {request?.message && (
            <div style={{ fontSize: "0.88rem", color: "var(--text-muted)", whiteSpace: "pre-line" }}>{request.message}</div>
          )}
          <div>
            <label className="form-label" htmlFor="danger-confirm-password">🔒 Delete password</label>
            <input
              id="danger-confirm-password"
              ref={inputRef}
              type="password"
              className="form-control"
              autoComplete="off"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError("");
              }}
            />
          </div>
          {error && (
            <div style={{ background: "rgba(239, 68, 68, 0.12)", border: "1px solid rgba(239, 68, 68, 0.35)", padding: "0.55rem 0.85rem", borderRadius: "var(--radius-md)", color: "#fca5a5", fontSize: "0.85rem" }}>
              ⚠️ {error}
            </div>
          )}
          <div style={{ display: "flex", gap: "0.75rem" }}>
            <Button type="button" variant="secondary" onClick={() => close(false)} style={{ flex: 1 }}>
              Cancel
            </Button>
            <Button type="submit" variant="danger" loading={checking} style={{ flex: 1 }}>
              {request?.confirmLabel || "Delete"}
            </Button>
          </div>
        </form>
      </BottomSheet>
    </DangerConfirmContext.Provider>
  );
}

export function useDangerConfirm() {
  const ctx = useContext(DangerConfirmContext);
  if (!ctx) {
    // Outside the provider (e.g. tests): behave like before
    return {
      confirmDanger: async ({ message, title } = {}) => window.confirm(message || title || "Are you sure?"),
      isProtected: false,
      verify: async () => true,
      refresh: async () => false
    };
  }
  return ctx;
}

export default DangerConfirmContext;
